import React, { useState } from "react"
import {
  Modal, Button, Upload, message, Alert, Spin,
} from "antd"
import { UploadOutlined, CloseOutlined } from "@ant-design/icons"
import csvIcon from "../../../assets/images/csv-icon.png"
import csvuploadIcon from "../../../assets/images/export.png"
import csvimportIcon from "../../../assets/images/import.png"
import csvuploadwhiteIcon from "../../../assets/images/export-white.png"

const { Dragger } = Upload

const CSVUploadModal = ({
  visible, onClose, rubricId, onUploadSuccess,
}) => {
  const [fileList, setFileList] = useState([])
  const [previewData, setPreviewData] = useState([])
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const handleClose = () => {
    setFileList([])
    setPreviewData([])
    setErrors([])
    setShowPreview(false)
    onClose()
  }
  // add handleFileChange function to update fileList state when user selects a file
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList)
    // Clear previous errors when user selects a new file
    setErrors([])
  }

  const handleDownloadSample = () => {
    const link = document.createElement("a")
    link.href = "/Indicators_file_template.csv"
    link.download = "Indicators_file_template.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    message.success("Sample CSV downloaded successfully")
  }

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.error("Please select a CSV file")
      return
    }

    // Additional validation for empty file
    if (fileList[0].size === 0) {
      message.error("File is empty. Please select a valid CSV file.")
      return
    }

    const formData = new FormData()
    formData.append("file", fileList[0].originFileObj)

    setLoading(true)

    APIClient.post(
      `/implementation_rubrics/${rubricId}/upload_csv`,
      formData,
      (response) => {
        setLoading(false)

        // Check if there are validation errors
        if (response.errors && response.errors.length > 0) {
          setErrors(response.errors)
          message.error("CSV validation failed. Please fix the errors and try again.")
          return
        }

        // Success case - show preview
        setPreviewData(response.data)
        setErrors([])
        setShowPreview(true)
        message.success("CSV file parsed successfully")
      },
      (error) => {
        setLoading(false)
        // Clear the file from dragger when validation errors occur
        setFileList([])
        // Handle 422 validation errors
        if (error.errors && Array.isArray(error.errors)) {
          setErrors(error.errors)
          message.error("CSV validation failed. Please fix the errors and try again.")
        } else {
          // Extract error message from object or use as string
          const errorMessage = error && typeof error === "object" && error.error
            ? error.error
            : (error || "Failed to upload file")
          message.error(errorMessage)
          setErrors([errorMessage])
        }
      },
    )
  }

  const handleConfirmUpload = () => {
    setLoading(true)

    APIClient.post(
      `/implementation_rubrics/${rubricId}/bulk_update_from_csv`,
      { indicators: previewData },
      (response) => {
        message.success(response.message || "Indicators updated successfully")
        if (onUploadSuccess) {
          onUploadSuccess()
        }
        handleClose()
        setLoading(false)
      },
      (error) => {
        message.error(error || "Failed to update indicators")
        setLoading(false)
      },
    )
  }

  // const uploadProps = {
  //   fileList,
  //   onChange: handleFileChange,
  //   beforeUpload: () => false,
  //   accept: ".csv",
  //   maxCount: 1,
  // }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      width={showPreview ? 900 : 800}
      footer={null}
      closable
      closeIcon={<CloseOutlined />}
      className="modal-header-csv"
      centered
    >
      {!showPreview ? (
        <div className="csv-upload-modal__initial">
          <div className="csv-upload-modal__header-initial">
            <span className="csv-upload-modal__title-initial">
              Upload Indicators (CSV)
            </span>
            <p className="csv-upload-modal__subtitle">
              Review parsed indicator data below. Upload a CSV file or use
              sample format to get started.
            </p>
          </div>

          <div className="csv-upload-modal__requirements">
            <span className="csv-upload-modal__requirements-title">
              CSV must include the following columns:
            </span>
            <div className="csv-upload-modal__requirements-list">
              <div className="csv-upload-modal__requirement-item">
                <span className="csv-upload-modal__bullet">•</span>
                <span>Indicator Number</span>
              </div>

              <div className="csv-upload-modal__requirement-item">
                <span className="csv-upload-modal__bullet">•</span>
                <span>Score (0-4)</span>
              </div>
              <div className="csv-upload-modal__requirement-item">
                <span className="csv-upload-modal__bullet">•</span>
                <span>Description</span>
              </div>
            </div>
            <p className="csv-upload-modal__requirements-note">
              (Tip: Scores should be numeric values between 0 and 4.)
            </p>
          </div>

          <Dragger
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept=".csv"
            maxCount={1}
            className="csv-upload-modal__dragger"
            showUploadList={false}
          >
            {fileList.length === 0 && !loading ? (
              <>
                <p className="csv-upload-modal__upload-icon">
                  <img
                    src={csvuploadIcon}
                    className="csv-upload-modal__file-icon"
                  />
                </p>
                <p className="csv-upload-modal__upload-text">
                  Click to upload or drag & drop
                </p>
                <p className="csv-upload-modal__upload-hint">CSV</p>
              </>
            ) : loading ? (
              <div style={{ padding: "40px" }}>
                <Spin size="large" tip="Validating CSV file..." />
              </div>
            ) : (
              <div className="csv-upload-modal__selected-file-display">
                <p className="csv-upload-modal__upload-icon">
                  <UploadOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                </p>
                <p
                  className="csv-upload-modal__upload-text"
                  style={{ color: "#1890ff" }}
                >
                  {fileList[0].name}
                </p>
                <p className="csv-upload-modal__upload-hint">
                  Click to change file
                </p>
              </div>
            )}
          </Dragger>

          {/* Error Messages Display */}
          {errors.length > 0 && !loading && (
            <div style={{ marginTop: "16px" }}>
              <Alert
                message="CSV Validation Errors"
                description={(
                  <div>
                    {errors.map((error, index) => (
                      <div key={index} style={{ marginBottom: "4px" }}>
                        • {error}
                      </div>
                    ))}
                  </div>
                )}
                type="error"
                showIcon
                closable
                onClose={() => setErrors([])}
              />
            </div>
          )}

          <div className="csv-upload-modal__actions-initial">
            <Button
              type="primary"
              icon={(
                <img
                  src={
                    fileList.length === 0 ? csvuploadIcon : csvuploadwhiteIcon
                  }
                  className="csv-upload-modal__file-icon"
                  style={{ width: "16px", height: "16px", marginRight: "8px" }}
                />
              )}
              onClick={handleUpload}
              loading={loading}
              disabled={loading || fileList.length === 0}
              className="csv-upload-modal__upload-btn-initial"
              size="large"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Upload CSV File
            </Button>
            <Button
              icon={(
                <img
                  src={csvimportIcon}
                  className="csv-upload-modal__file-icon"
                  style={{ width: "16px", height: "16px", marginRight: "8px" }}
                />
              )}
              onClick={handleDownloadSample}
              className="csv-upload-modal__download-btn-initial"
              size="large"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Download Sample CSV
            </Button>
          </div>
        </div>
      ) : (
        <Spin spinning={loading}>
          <div>
            <div className="csv-upload-modal__header">
              <span className="csv-upload-modal__title">
                Preview Rubric Content
              </span>
              <span className="csv-upload-modal__description-text">
                Review parsed indicator content below. Click &apos;Insert
                Indicators&apos; to add this content to the corresponding
                fields.
              </span>
            </div>

            <div className="csv-upload-modal__file-info">
              <img src={csvIcon} className="csv-upload-modal__file-icon" />
              <span className="csv-upload-modal__file-name">
                {fileList.length > 0 ? fileList[0].name : "CSV File"}
              </span>
            </div>

            {errors.length > 0 && (
              <Alert
                message="Warnings"
                description={(
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                )}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <div className="csv-upload-modal__indicators-list">
              {previewData.map((item, index) => (
                <div key={index} className="csv-upload-modal__indicator-card">
                  <div className="csv-upload-modal__indicator-header">
                    <div className="csv-upload-modal__indicator-left">
                      <div className="csv-upload-modal__indicator-number">
                        {item.index}
                      </div>
                      <span className="csv-upload-modal__indicator-label">
                        Indicator
                      </span>
                    </div>
                    <div className="csv-upload-modal__indicator-score">
                      Score: {item.score}
                    </div>
                  </div>

                  {item.description && (
                    <div className="csv-upload-modal__indicator-description">
                      {item.description}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="csv-upload-modal__actions">
              <Button
                onClick={handleClose}
                className="csv-upload-modal__cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUpload}
                className="csv-upload-modal__upload-btn"
                disabled={previewData.length === 0}
              >
                Upload Indicators
              </Button>
            </div>
          </div>
        </Spin>
      )}
    </Modal>
  )
}

export default CSVUploadModal
